import React, { useState, useEffect, useRef } from 'react';
import { X, Users, Search, Check, ChevronDown } from 'lucide-react';
import { api } from '../api';
import './ActivityModal.css';

const CATEGORIES = ['home', 'errands', 'finance', 'health', 'work', 'personal', 'educational', 'reference'];
const PRIORITIES = ['high', 'medium', 'low'];
const RECURRENCE_TYPES = ['daily', 'weekly', 'monthly', 'yearly'];
const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun', short: 'S' },
  { value: 1, label: 'Mon', short: 'M' },
  { value: 2, label: 'Tue', short: 'T' },
  { value: 3, label: 'Wed', short: 'W' },
  { value: 4, label: 'Thu', short: 'T' },
  { value: 5, label: 'Fri', short: 'F' },
  { value: 6, label: 'Sat', short: 'S' }
];

// Category points mapping (read-only)
const CATEGORY_POINTS = {
  errands: 1,
  finance: 5,
  health: 5,
  reference: 10,
  work: 1,
  personal: 3,
  educational: 5,
  home: 1 // default
};

export default function ActivityModal({ activity, onSave, onClose, currentUser }) {
  const [form, setForm] = useState({
    title: '',
    category: 'home',
    priority: 'medium',
    status: 'pending',
    dueDate: '',
    notes: '',
    isRecurring: false,
    recurrenceType: 'weekly',
    recurrenceInterval: 1,
    recurrenceDays: [], // Array of day numbers for weekly recurrence
    recurrenceEndDate: '',
    assignedUsers: [],
    points: 1,
    attachments: [], // Array of {type, url, description} for reference materials
    reminderDays: null, // Email reminder: 5, 3, or 1 days before due date
  });
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const titleRef = useRef(null);
  
  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    if (activity) {
      setForm({
        title: activity.title || '',
        category: activity.category || 'home',
        priority: activity.priority || 'medium',
        status: activity.status || 'pending',
        dueDate: activity.dueDate || '',
        notes: activity.notes || '',
        isRecurring: activity.isRecurring || false,
        recurrenceType: activity.recurrenceType || 'weekly',
        recurrenceInterval: activity.recurrenceInterval || 1,
        recurrenceDays: activity.recurrenceDays || [],
        recurrenceEndDate: activity.recurrenceEndDate || '',
        assignedUsers: activity.assignedUsers || [],
        points: activity.points || CATEGORY_POINTS[activity.category] || 1,
        attachments: activity.attachments || [],
        reminderDays: activity.reminderDays || null,
      });
    }
    setTimeout(() => titleRef.current?.focus(), 50);
  }, [activity]);

  // Auto-update points when category changes
  useEffect(() => {
    const points = CATEGORY_POINTS[form.category] || 1;
    setForm(prev => ({ ...prev, points }));
  }, [form.category]);
  
  // Fetch users for admin assignment
  useEffect(() => {
    if (isAdmin) {
      setLoadingUsers(true);
      api.getUsers()
        .then(data => setUsers(data.users || []))
        .catch(err => console.error('Failed to load users:', err))
        .finally(() => setLoadingUsers(false));
    }
  }, [isAdmin]);

  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef(null);

  const toggleUserAssignment = (userId) => {
    setForm(prev => {
      const current = prev.assignedUsers || [];
      const updated = current.includes(userId)
        ? current.filter(id => id !== userId)
        : [...current, userId];
      return { ...prev, assignedUsers: updated };
    });
  };

  const removeAssignedUser = (userId) => {
    setForm(prev => ({
      ...prev,
      assignedUsers: (prev.assignedUsers || []).filter(id => id !== userId)
    }));
  };

  // Filter users based on search
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(userSearch.toLowerCase()) ||
    user.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    onSave(form);
  };

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdrop}>
      <div className="modal fade-in">
        <div className="modal-header">
          <h2 className="modal-title">{activity ? 'Edit Activity' : 'New Activity'}</h2>
          <div className="modal-top-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" form="activity-form">
              {activity ? 'Save Changes' : 'Create Activity'}
            </button>
          </div>
        </div>
        <form id="activity-form" className="modal-form" onSubmit={handleSubmit}>
          <div className="field">
            <label className="field-label">Title <span className="required">*</span></label>
            <input
              ref={titleRef}
              type="text"
              className="field-input"
              placeholder="What needs to be done?"
              value={form.title}
              onChange={e => set('title', e.target.value)}
              required
            />
          </div>

          <div className="field-row">
            <div className="field">
              <label className="field-label">Category</label>
              <select className="field-select" value={form.category} onChange={e => set('category', e.target.value)}>
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="field-label">Priority</label>
              <select className={`field-select prio-${form.priority}`} value={form.priority} onChange={e => set('priority', e.target.value)}>
                {PRIORITIES.map(p => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="field-label">Points</label>
              <div className="field-readonly points-display">
                <span className="points-value">{form.points}</span>
                <span className="points-label">{form.points === 1 ? 'pt' : 'pts'}</span>
              </div>
              <p className="field-help">Auto-assigned by category</p>
            </div>

            <div className="field">
              <label className="field-label">Status</label>
              <select className="field-select" value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          <div className="field">
            <label className="field-label">Due Date</label>
            <input
              type="date"
              className="field-input"
              value={form.dueDate}
              onChange={e => set('dueDate', e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field-label">Email Reminder</label>
            <select 
              className="field-select"
              value={form.reminderDays || ''}
              onChange={e => set('reminderDays', e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">No reminder</option>
              <option value="5">5 days before</option>
              <option value="3">3 days before</option>
              <option value="1">1 day before</option>
            </select>
            <p className="field-help">Send email notification before due date</p>
          </div>

          <div className="field">
            <label className="field-label">Notes</label>
            <textarea
              className="field-textarea"
              placeholder="Optional notes or details…"
              rows={3}
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
            />
          </div>

          {/* Attachments Section - only for reference category */}
          {form.category === 'reference' && (
            <div className="attachments-section">
              <div className="field">
                <label className="field-label">Attachments & Links <span className="required">*</span></label>
                <p className="field-help">Add URLs to videos, articles, documents, or other resources</p>
                
                {form.attachments.map((att, index) => (
                  <div key={index} className="attachment-row">
                    <select 
                      className="attachment-type"
                      value={att.type}
                      onChange={e => {
                        const newAttachments = [...form.attachments];
                        newAttachments[index].type = e.target.value;
                        setForm(prev => ({ ...prev, attachments: newAttachments }));
                      }}
                    >
                      <option value="url">URL/Link</option>
                      <option value="video">Video</option>
                      <option value="youtube">YouTube</option>
                      <option value="article">Article</option>
                      <option value="document">Document</option>
                      <option value="investment">Investment</option>
                      <option value="educational">Educational</option>
                    </select>
                    <input
                      type="text"
                      className="attachment-url"
                      placeholder="https://..."
                      value={att.url}
                      onChange={e => {
                        const newAttachments = [...form.attachments];
                        newAttachments[index].url = e.target.value;
                        setForm(prev => ({ ...prev, attachments: newAttachments }));
                      }}
                    />
                    <input
                      type="text"
                      className="attachment-desc"
                      placeholder="Description (optional)"
                      value={att.description || ''}
                      onChange={e => {
                        const newAttachments = [...form.attachments];
                        newAttachments[index].description = e.target.value;
                        setForm(prev => ({ ...prev, attachments: newAttachments }));
                      }}
                    />
                    <button
                      type="button"
                      className="attachment-remove"
                      onClick={() => {
                        const newAttachments = form.attachments.filter((_, i) => i !== index);
                        setForm(prev => ({ ...prev, attachments: newAttachments }));
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                
                <button
                  type="button"
                  className="btn-add-attachment"
                  onClick={() => {
                    setForm(prev => ({
                      ...prev,
                      attachments: [...prev.attachments, { type: 'url', url: '', description: '' }]
                    }));
                  }}
                >
                  + Add Link/Attachment
                </button>
              </div>
            </div>
          )}

          {/* Recurrence Section */}
          <div className="recurrence-section">
            <div className="field recurrence-toggle">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.isRecurring}
                  onChange={e => set('isRecurring', e.target.checked)}
                />
                <span className="checkbox-text">Make this a recurring task</span>
              </label>
              <p className="recurrence-help">
                When completed, this task will reset for the next period
              </p>
            </div>

            {form.isRecurring && (
              <div className="recurrence-options">
                <div className="field-row">
                  <div className="field">
                    <label className="field-label">Repeat</label>
                    <select
                      className="field-select"
                      value={form.recurrenceType}
                      onChange={e => set('recurrenceType', e.target.value)}
                    >
                      {RECURRENCE_TYPES.map(type => (
                        <option key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="field">
                    <label className="field-label">Every</label>
                    <div className="interval-input">
                      <input
                        type="number"
                        min="1"
                        max="52"
                        value={form.recurrenceInterval}
                        onChange={e => set('recurrenceInterval', parseInt(e.target.value) || 1)}
                        className="field-input number-input"
                      />
                      <span className="interval-label">{form.recurrenceType}{form.recurrenceInterval > 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>

                {/* Day of Week Selector - Only for weekly recurrence */}
                {form.recurrenceType === 'weekly' && (
                  <div className="field">
                    <label className="field-label">Repeat on Days</label>
                    <div className="days-selector">
                      {DAYS_OF_WEEK.map(day => (
                        <button
                          key={day.value}
                          type="button"
                          className={`day-button ${form.recurrenceDays.includes(day.value) ? 'active' : ''}`}
                          onClick={() => {
                            const currentDays = form.recurrenceDays || [];
                            const isSelected = currentDays.includes(day.value);
                            const newDays = isSelected
                              ? currentDays.filter(d => d !== day.value)
                              : [...currentDays, day.value].sort();
                            set('recurrenceDays', newDays);
                          }}
                          title={day.label}
                        >
                          {day.short}
                        </button>
                      ))}
                    </div>
                    <p className="field-help">Select which days of the week this chore should repeat</p>
                  </div>
                )}

                <div className="field">
                  <label className="field-label">End Date (Optional)</label>
                  <input
                    type="date"
                    className="field-input"
                    value={form.recurrenceEndDate}
                    onChange={e => set('recurrenceEndDate', e.target.value)}
                    placeholder="Leave empty for no end date"
                  />
                  <p className="field-help">When the task should stop recurring</p>
                </div>
              </div>
            )}
          </div>

          {/* User Assignment Section - Admin Only */}
          {isAdmin && (
            <div className="assignment-section" ref={userDropdownRef}>
              <div className="field">
                <label className="field-label">
                  <Users size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Assign to Users
                </label>
                {loadingUsers ? (
                  <p className="loading-text">Loading users...</p>
                ) : users.length === 0 ? (
                  <p className="no-users-text">No users available</p>
                ) : (
                  <div className="user-select-container">
                    {/* Selected Users Tags */}
                    {(form.assignedUsers || []).length > 0 && (
                      <div className="selected-users">
                        {form.assignedUsers.map(userId => {
                          const user = users.find(u => u.id === userId);
                          return user ? (
                            <span key={userId} className="user-tag">
                              <span className="user-tag-name">{user.name}</span>
                              <button
                                type="button"
                                className="user-tag-remove"
                                onClick={() => removeAssignedUser(userId)}
                                title="Remove"
                              >
                                <X size={12} />
                              </button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}

                    {/* Dropdown Trigger */}
                    <div
                      className="user-dropdown-trigger"
                      onClick={() => setShowUserDropdown(!showUserDropdown)}
                    >
                      <span className="dropdown-placeholder">
                        {(form.assignedUsers || []).length === 0
                          ? 'Select users to assign...'
                          : `${form.assignedUsers.length} user(s) selected`}
                      </span>
                      <ChevronDown size={16} className={showUserDropdown ? 'rotate' : ''} />
                    </div>

                    {/* Dropdown Menu */}
                    {showUserDropdown && (
                      <div className="user-dropdown-menu">
                        {/* Search Input */}
                        <div className="user-search-wrapper">
                          <Search size={14} className="search-icon" />
                          <input
                            type="text"
                            className="user-search-input"
                            placeholder="Search users by name or email..."
                            value={userSearch}
                            onChange={e => setUserSearch(e.target.value)}
                            autoFocus
                          />
                          {userSearch && (
                            <button
                              type="button"
                              className="clear-search"
                              onClick={() => setUserSearch('')}
                            >
                              <X size={12} />
                            </button>
                          )}
                        </div>

                        {/* User List */}
                        <div className="user-dropdown-list">
                          {filteredUsers.length === 0 ? (
                            <div className="no-results">No users found</div>
                          ) : (
                            filteredUsers.map(user => {
                              const isSelected = (form.assignedUsers || []).includes(user.id);
                              return (
                                <div
                                  key={user.id}
                                  className={`user-option ${isSelected ? 'selected' : ''}`}
                                  onClick={() => toggleUserAssignment(user.id)}
                                >
                                  <div className="user-option-check">
                                    {isSelected && <Check size={14} />}
                                  </div>
                                  <div className="user-option-info">
                                    <span className="user-option-name">{user.name}</span>
                                    <span className="user-option-email">{user.email}</span>
                                  </div>
                                  <span className={`user-option-role role-${user.role}`}>
                                    {user.role}
                                  </span>
                                </div>
                              );
                            })
                          )}
                        </div>

                        {/* Dropdown Footer */}
                        <div className="user-dropdown-footer">
                          <span>{filteredUsers.length} user(s) found</span>
                          <button
                            type="button"
                            className="done-btn"
                            onClick={() => setShowUserDropdown(false)}
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <p className="field-help">
                  Search and select users to assign this task to. Click Done when finished.
                </p>
              </div>
            </div>
          )}

          <div className="modal-footer modal-footer-sticky">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">{activity ? 'Save Changes' : 'Add Activity'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
